import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Pagination,
  CircularProgress,
  Stack,
  Divider
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ClearIcon from "@mui/icons-material/Clear";
import TaggingService from "../../services/TaggingService";

const SalesInvoiceReport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [summary, setSummary] = useState({
    invoices: 0,
    gross: 0,
    net: 0,
    gst: 0
  });

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    sale_type: "",
    q: "",
    page: 1,
    page_size: 20
  });

  const fetchReports = async (customFilters = filters) => {
    try {
      setLoading(true);

      const res = await TaggingService.getDeviceTagInvoice(customFilters);

      const list = res?.data?.results?.results || [];
      const count = res?.data?.count || 0;

      setRows(list);
      setTotalPages(Math.ceil(count / customFilters.page_size));

      let gross = 0;
      let net = 0;
      let gst = 0;

      list.forEach((item) => {
        gross += Number(item.gross_amount || 0);
        net += Number(item.net_amount || 0);
        gst +=
          Number(item.cgst_9_percent || 0) +
          Number(item.sgst_9_percent || 0) +
          Number(item.igst_18_percent || 0);
      });

      setSummary({
        invoices: count,
        gross,
        net,
        gst
      });
    } catch (error) {
      console.log("Report Error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    const updated = { ...filters, page: 1 };
    setPage(1);
    setFilters(updated);
    fetchReports(updated);
  };

  const handleClear = () => {
  const defaultFilter = {
    start_date: "",
    end_date: "",
    sale_type: "",
    q: "",
    page: 1,
    page_size: 20
  };

  setFilters(defaultFilter);
  setPage(1);
  fetchReports(defaultFilter);
};

  const handlePageChange = (_, value) => {
    const updated = { ...filters, page: value };
    setPage(value);
    setFilters(updated);
    fetchReports(updated);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ p: 3, background: "#f4f6f8", minHeight: "100vh" }}>
      {/* Header */}
      <Typography variant="h4" fontWeight={700} mb={3}>
        Sales / Invoice Report
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          {
            title: "Total Invoices",
            value: summary.invoices
          },
          {
            title: "Net Amount",
            value: `₹ ${summary.net.toFixed(2)}`
          },
          {
            title: "GST Amount",
            value: `₹ ${summary.gst.toFixed(2)}`
          },
          {
            title: "Gross Amount",
            value: `₹ ${summary.gross.toFixed(2)}`
          }
        ].map((item, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card
              sx={{
                p: 2,
                borderRadius: "14px",
                boxShadow: 2
              }}
            >
              <Typography color="text.secondary" fontSize={14}>
                {item.title}
              </Typography>
              <Typography variant="h5" fontWeight={700} mt={1}>
                {item.value}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filter Card */}
      <Card sx={{ p: 2.5, mb: 3, borderRadius: "14px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Invoice / IMEI "
              name="q"
              value={filters.q}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              name="start_date"
              InputLabelProps={{ shrink: true }}
              value={filters.start_date}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              name="end_date"
              InputLabelProps={{ shrink: true }}
              value={filters.end_date}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Sale Type"
              name="sale_type"
              value={filters.sale_type}
              onChange={handleChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="within-state">Within State</MenuItem>
              <MenuItem value="inter-state">Inter State</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
  <Button
    fullWidth
    variant="contained"
    startIcon={<SearchIcon />}
    onClick={handleSearch}
  >
    Search
  </Button>

  <Button
    variant="outlined"
    startIcon={<PrintIcon />}
    onClick={handlePrint}
  >
    Print
  </Button>

  <Button
    variant="outlined"
    startIcon={<FileDownloadIcon />}
    onClick={handlePrint}
  >
    Export
  </Button>

  <Button
    variant="outlined"
    color="error"
    startIcon={<ClearIcon />}
    onClick={handleClear}
  >
    Clear
  </Button>
</Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: "14px", overflow: "hidden" }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#0b1e3b" }}>
                {[
                  "Invoice",
                  "Date",
                  "Customer",
                  "Dealer",
                  "IMEI",
                  "Sale Type",
                  "Net",
                  "CGST",
                  "SGST",
                  "IGST",
                  "Gross"
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      color: "#fff",
                      fontWeight: 700
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No Data Found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((item, index) => (
                  <TableRow hover key={index}>
                    <TableCell>{item.invoice_no}</TableCell>
                    <TableCell>{item.invoice_date}</TableCell>
                    <TableCell>{item.customer}</TableCell>
                    <TableCell>{item.dealer}</TableCell>
                    <TableCell>{item.device_imei_no}</TableCell>
                    <TableCell>{item.sale_type}</TableCell>
                    <TableCell>₹ {item.net_amount}</TableCell>
                    <TableCell>₹ {item.cgst_9_percent}</TableCell>
                    <TableCell>₹ {item.sgst_9_percent}</TableCell>
                    <TableCell>₹ {item.igst_18_percent}</TableCell>
                    <TableCell>
                      <b>₹ {item.gross_amount}</b>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Typography variant="body2">
            Showing {rows.length} records
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Card>
    </Box>
  );
};

export default SalesInvoiceReport;